/**
 * Host service control. Each service is a card with its live state and
 * start/stop/restart actions, driven by /api/admin/services/*.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ServiceAction, ServiceStatus } from '@hub/shared';
import { ApiError, api } from '../../api/client';
import { ActionButton, Alert, Card, Loading, Pill, useFlash } from '../../components/ui';

interface ServicesResponse { services: ServiceStatus[]; platform: string }
interface ActionResponse { ok: boolean; output: string; deferred?: boolean }
interface LogsResponse { logs: string }

type Tone = 'ok' | 'warn' | 'error';

/** Uptime reads forward ("up 3h"), unlike the "3h ago" used elsewhere. */
function fmtUptime(since: number): string {
  const d = Math.floor(Date.now() / 1000) - since;
  if (d < 60) return `up ${d}s`;
  if (d < 3600) return `up ${Math.floor(d / 60)}m`;
  if (d < 86400) return `up ${Math.floor(d / 3600)}h`;
  return `up ${Math.floor(d / 86400)}d`;
}

function stateColor(state: string): string {
  if (state === 'running') return 'var(--ha-accent-emerald)';
  if (state === 'failed') return 'var(--ha-accent-red)';
  if (state === 'stopped') return 'var(--ha-text-muted)';
  return 'var(--ha-warn)';
}

function toneColor(tone: Tone): string {
  if (tone === 'ok') return 'var(--ha-accent-emerald)';
  if (tone === 'warn') return 'var(--ha-warn)';
  return 'var(--ha-accent-red)';
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function metaLine(s: ServiceStatus): string {
  const meta: string[] = [];
  if (s.pid) meta.push(`pid ${s.pid}`);
  if (s.since) meta.push(fmtUptime(s.since));
  if (s.enabled === true) meta.push('enabled at boot');
  if (s.enabled === false) meta.push('not enabled at boot');
  return meta.length ? `${s.detail} · ${meta.join(' · ')}` : s.detail;
}

export function AdminServicesPage() {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [platform, setPlatform] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openLogs, setOpenLogs] = useState<string | null>(null);
  const { show: showFlash, node: flashNode } = useFlash();

  const reloadTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(reloadTimer.current), []);

  const load = useCallback(async () => {
    try {
      const r = await api.get<ServicesResponse>('/api/admin/services');
      setServices(r.services);
      setPlatform(r.platform);
      setLoadError(null);
    } catch (err) {
      setLoadError(errText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /** The hub restarting itself tears down this process, so don't poll for it. */
  const scheduleReload = useCallback(() => {
    window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => { void load(); }, 1200);
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
    showFlash('Refreshed', 'ok');
  }, [load, showFlash]);

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h2 style={{ color: 'var(--ha-text-primary)', fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Services
          </h2>
          {platform && <Pill tone="muted">{platform}</Pill>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            className="btn-ghost"
            style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
            onClick={() => { void refresh(); }}
          >
            Refresh
          </button>
          {flashNode}
        </div>
      </div>

      <p className="muted" style={{ fontSize: '0.8rem', marginBottom: '1.25rem' }}>
        Host services this hub depends on. Actions are limited to a fixed set of services and run through
        the system service manager.
      </p>

      {loadError && (
        <div style={{ marginBottom: '1rem' }}>
          <Alert tone="error">
            <span>{loadError}</span>
            <button
              type="button"
              className="btn-ghost"
              style={{ marginLeft: '0.75rem', padding: '0.15rem 0.6rem', fontSize: '0.8rem' }}
              onClick={() => { void load(); }}
            >
              Retry
            </button>
          </Alert>
        </div>
      )}

      {loading && <Loading />}

      {services.map((s) => (
        <ServiceCard
          key={s.id}
          service={s}
          logsOpen={openLogs === s.id}
          onToggleLogs={() => setOpenLogs((cur) => (cur === s.id ? null : s.id))}
          onActionDone={scheduleReload}
        />
      ))}
    </div>
  );
}

function ServiceCard({ service, logsOpen, onToggleLogs, onActionDone }: {
  service: ServiceStatus;
  logsOpen: boolean;
  onToggleLogs: () => void;
  onActionDone: () => void;
}) {
  const [msg, setMsg] = useState<{ text: string; tone: Tone } | null>(null);
  const msgTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(msgTimer.current), []);

  // Permission guidance is multi-line and worth reading — leave it up longer.
  const showMsg = useCallback((text: string, tone: Tone) => {
    window.clearTimeout(msgTimer.current);
    setMsg({ text, tone });
    msgTimer.current = window.setTimeout(() => setMsg(null), tone === 'warn' ? 12000 : 6000);
  }, []);

  const run = useCallback(async (action: ServiceAction) => {
    try {
      const r = await api.post<ActionResponse>(
        `/api/admin/services/${encodeURIComponent(service.id)}/${action}`,
      );
      showMsg(r.output || `${service.name} ${action} issued`, 'ok');
      if (!r.deferred) onActionDone();
    } catch (err) {
      const permission = err instanceof ApiError && err.permission === true;
      showMsg(errText(err), permission ? 'warn' : 'error');
      throw err;
    }
  }, [service.id, service.name, showMsg, onActionDone]);

  // Destructive actions state their consequence before asking.
  const confirmFor = (verb: string) =>
    service.caution ? `${verb} ${service.name}?\n\n${service.caution}` : `${verb} ${service.name}?`;

  return (
    <Card style={{ marginBottom: '1rem' }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: '1rem', flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: '16rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: stateColor(service.state), fontSize: '0.9rem' }}>●</span>
            <strong style={{ color: 'var(--ha-text-primary)', fontSize: '0.95rem' }}>{service.name}</strong>
            <Pill tone="muted">{service.state}</Pill>
            {service.selfHosting && (
              <Pill tone="muted" title="Acting on this restarts the hub itself">self</Pill>
            )}
          </div>
          <div className="muted" style={{ fontSize: '0.78rem', marginTop: '0.3rem' }}>{service.description}</div>
          <div className="muted mono" style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>{metaLine(service)}</div>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {service.controllable ? (
            <>
              <ActionButton
                busyLabel="Restarting…"
                confirm={confirmFor('Restart')}
                onClick={() => run('restart')}
              >
                Restart
              </ActionButton>
              {service.state === 'running' ? (
                <ActionButton
                  danger
                  busyLabel="Stopping…"
                  confirm={confirmFor('Stop')}
                  onClick={() => run('stop')}
                >
                  Stop
                </ActionButton>
              ) : (
                <ActionButton busyLabel="Starting…" onClick={() => run('start')}>Start</ActionButton>
              )}
            </>
          ) : (
            <span className="muted" style={{ fontSize: '0.78rem' }}>
              {service.reason || 'Not controllable here'}
            </span>
          )}
          {service.logsAvailable && (
            <button type="button" className="btn-ghost btn-sm" onClick={onToggleLogs}>Logs</button>
          )}
        </div>
      </div>

      {msg && (
        <div style={{
          fontSize: '0.78rem', marginTop: '0.5rem', whiteSpace: 'pre-wrap', color: toneColor(msg.tone),
        }}>
          {msg.text}
        </div>
      )}

      {logsOpen && <ServiceLogs id={service.id} />}
    </Card>
  );
}

function ServiceLogs({ id }: { id: string }) {
  const [text, setText] = useState('Loading…');

  useEffect(() => {
    let live = true;
    setText('Loading…');
    api.get<LogsResponse>(`/api/admin/services/${encodeURIComponent(id)}/logs?lines=200`)
      .then((r) => { if (live) setText(r.logs || '(no output)'); })
      .catch((err: unknown) => { if (live) setText(errText(err)); });
    return () => { live = false; };
  }, [id]);

  return <pre className="output" style={{ marginTop: '0.75rem' }}>{text}</pre>;
}
